// An administration's bank delivery, cash ledger and settlement ledger, on its
// card on the Administrations screen. Loaded when opened, not with the list.
//
// The owner and the agency operator can change the three; reviewers and
// viewers read them. The server decides
// that too (403), and says so; the form only avoids offering what will be refused.
//
// `verificationTrial` is shown and never offered: it opens an unverified write
// class, so it changes only through the control plane (`cli.ts trial-set`).

import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, newIdempotencyKey, request, tenantPath } from '../api/client.ts';
import type { AdministrationSettings as Settings } from '../api/types.ts';

type Load = { kind: 'closed' } | { kind: 'loading' } | { kind: 'ready'; settings: Settings } | { kind: 'error'; message: string };

const DELIVERY_LABEL: Readonly<Record<string, string>> = {
  live_feed: 'Live bank feed in the accounting package (we never post payments)',
  mt940: 'MT940 statements imported here (we post payments)',
};

function ledgerLabel(settings: Settings, id: string | null): string {
  if (id === null) return 'Not set';
  const ledger = settings.ledgers.find((l) => l.id === id);
  return ledger === undefined ? `${id} (not in the synced chart)` : `${ledger.code} ${ledger.name ?? ''}`.trim();
}

function failure(caught: unknown): string {
  if (!(caught instanceof ApiError)) return 'The settings could not be saved.';
  if (caught.status === 409) return 'These settings changed since they were opened, so nothing was saved. Close and open them again.';
  if (caught.status === 403) return 'Only the office owner or the agency operator can change these.';
  if (caught.status === 422) {
    return `Refused: ${caught.fieldErrors.map((f) => `${f.field} (${f.code})`).join(', ') || 'validation failed'}.`;
  }
  return caught.message;
}

export function AdministrationSettings({
  token,
  tenantId,
  administrationId,
  canEdit,
}: {
  readonly token: string;
  readonly tenantId: string;
  readonly administrationId: string;
  readonly canEdit: boolean;
}): ReactNode {
  const [load, setLoad] = useState<Load>({ kind: 'closed' });
  const [draft, setDraft] = useState<{ bankDelivery: string; cashLedgerId: string; settlementLedgerId: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const path = tenantPath(tenantId, `/administrations/${administrationId}/settings`);

  const show = useCallback(
    (settings: Settings) => {
      setLoad({ kind: 'ready', settings });
      setDraft({
        bankDelivery: settings.bankDelivery ?? 'live_feed',
        cashLedgerId: settings.cashLedgerId ?? '',
        settlementLedgerId: settings.settlementLedgerId ?? '',
      });
    },
    [],
  );

  const open = useCallback(async () => {
    setLoad({ kind: 'loading' });
    setNotice(null);
    try {
      show(await request<Settings>(path, { token }));
    } catch (caught) {
      setLoad({ kind: 'error', message: caught instanceof ApiError && caught.isAbsent ? 'No longer available to your session.' : 'Could not be read.' });
    }
  }, [path, token, show]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (load.kind !== 'ready' || draft === null) return;
    setBusy(true);
    setNotice(null);
    try {
      const saved = await request<Settings>(path, {
        method: 'PATCH',
        token,
        idempotencyKey: newIdempotencyKey(),
        body: {
          expectedVersion: load.settings.version,
          bankDelivery: draft.bankDelivery,
          cashLedgerId: draft.cashLedgerId === '' ? null : draft.cashLedgerId,
          settlementLedgerId: draft.settlementLedgerId === '' ? null : draft.settlementLedgerId,
        },
      });
      show(saved);
      setNotice(saved.version === load.settings.version ? 'Nothing changed.' : 'Saved. The change is in the audit trail.');
    } catch (caught) {
      setNotice(failure(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <details
      onToggle={(event) => {
        if ((event.currentTarget as HTMLDetailsElement).open && load.kind !== 'ready') void open();
      }}
    >
      <summary>Settings</summary>
      {load.kind === 'loading' ? <p className="muted small">Loading…</p> : null}
      {load.kind === 'error' ? <p className="muted small">{load.message}</p> : null}
      {load.kind === 'ready' && draft !== null ? (
        canEdit ? (
          <form className="stack small" onSubmit={(event) => void save(event)}>
            <label>
              Bank transactions{' '}
              <select
                value={draft.bankDelivery}
                disabled={busy}
                onChange={(event) => setDraft({ ...draft, bankDelivery: event.target.value })}
              >
                {Object.entries(DELIVERY_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            {draft.bankDelivery === 'mt940' && load.settings.bankDelivery !== 'mt940' ? (
              <p className="callout warn small">
                Only if this administration has NO live bank feed in the accounting package. Otherwise the
                same payment is booked twice.
              </p>
            ) : null}
            {(['cashLedgerId', 'settlementLedgerId'] as const).map((field) => (
              <label key={field}>
                {field === 'cashLedgerId' ? 'Cash ledger (cash receipts)' : 'Settlement ledger (credit-note settlements)'}{' '}
                <select
                  value={draft[field]}
                  disabled={busy}
                  onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
                >
                  <option value="">Not set</option>
                  {load.settings.ledgers.map((ledger) => (
                    <option key={ledger.id} value={ledger.id}>{`${ledger.code} ${ledger.name ?? ''}`.trim()}</option>
                  ))}
                </select>
              </label>
            ))}
            <p className="muted small">
              Verification trial: <b>{load.settings.verificationTrial ? 'on' : 'off'}</b> (changed by the operator only)
            </p>
            <button type="submit" className="btn s" disabled={busy}>Save settings</button>
            {notice === null ? null : <p className="small" role="status">{notice}</p>}
          </form>
        ) : (
          <dl className="small">
            <dt>Bank transactions</dt>
            <dd>{load.settings.bankDelivery === null ? 'Not set (treated as a live feed)' : DELIVERY_LABEL[load.settings.bankDelivery]}</dd>
            <dt>Cash ledger</dt>
            <dd>{ledgerLabel(load.settings, load.settings.cashLedgerId)}</dd>
            <dt>Settlement ledger</dt>
            <dd>{ledgerLabel(load.settings, load.settings.settlementLedgerId)}</dd>
            <dt>Verification trial</dt>
            <dd>{load.settings.verificationTrial ? 'On' : 'Off'} (changed by the operator only)</dd>
          </dl>
        )
      ) : null}
    </details>
  );
}
